'use client';

import { c as _c } from "react/compiler-runtime";
import cx from 'classix';
import { formatDistance } from 'date-fns';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { type MouseEvent, useState } from 'react';
import ListingDetailAllPhotos from '@/features/listing/components/listing-detailed/listing-detail-gallery/listing-detail-all-photos/listing-detail-all-photos';
import { LISTINGS_TYPES_TO_ID } from '@/features/listing/constants/listing-constants';
import createListingBookmark from '@/features/listing/requests/create-listing-bookmark';
import deleteListingBookmark from '@/features/listing/requests/delete-listing-bookmark';
import { type Listing, type ListingType } from '@/features/listing/types/listing-types';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import { useRouter } from '@/i18n/routing';
import getDateFnsLocale from '@/libs/date-fns/get-date-fns-locale';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import { type ImageThumbnail } from '@/shared/types/image-thumbnail-types';
import styles from './listing-detail-gallery.module.scss';
export interface ListingDetailGalleryProps {
  images: ImageThumbnail[];
  alt: string;
  isNew: Listing['isNew'];
  isBookmarked: Listing['isBookmarked'];
  listingRefreshedDate: Listing['refreshed'];
  listingId: Listing['id'];
  listingType: ListingType;
}
export default function ListingDetailGallery(props) {
  const $ = _c(38);
  const {
    alt,
    images,
    isBookmarked,
    isNew,
    listingId,
    listingRefreshedDate,
    listingType
  } = props;
  const listingDetailTranslations = useTranslations("listing.detail");
  const listingLabelsTranslations = useTranslations("listing.labels");
  const locale = useLocale();
  const router = useRouter();
  const [isAllPhotosShow, setIsAllPhotosShow] = useState(false);
  const [isListingBookmarked, setIsListingBookmarked] = useState(isBookmarked);
  const mainImage = images[0];
  let handleBookmarkClick;
  let handleHideAllPhotos;
  let t0;
  if ($[0] !== alt || $[1] !== images || $[2] !== isListingBookmarked || $[3] !== isNew || $[4] !== listingDetailTranslations || $[5] !== listingId || $[6] !== listingLabelsTranslations || $[7] !== listingRefreshedDate || $[8] !== listingType || $[9] !== locale || $[10] !== mainImage || $[11] !== router) {
    const externalImages = images.slice(1, 5);
    let t1;
    if ($[15] !== listingRefreshedDate || $[16] !== locale) {
      t1 = formatDistance(listingRefreshedDate, new Date(), {
        addSuffix: true,
        locale: getDateFnsLocale(locale)
      });
      $[15] = listingRefreshedDate;
      $[16] = locale;
      $[17] = t1;
    } else {
      t1 = $[17];
    }
    const relativeRefreshDate = t1;
    let t2;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = () => {
        setIsAllPhotosShow(true);
      };
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    const handleShowAllPhotos = t2;
    let t3;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = () => {
        setIsAllPhotosShow(false);
      };
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    handleHideAllPhotos = t3;
    let t4;
    if ($[20] !== isListingBookmarked || $[21] !== listingId || $[22] !== listingType || $[23] !== router) {
      t4 = event => {
        event.stopPropagation();
        event.preventDefault();
        const loggedUser = getUserBearerCookie();
        if (loggedUser) {
          const currentListingTypeId = LISTINGS_TYPES_TO_ID[listingType];
          if (isListingBookmarked) {
            setIsListingBookmarked(false);
            deleteListingBookmark(listingId, currentListingTypeId).catch(_temp);
          } else {
            setIsListingBookmarked(true);
            createListingBookmark(listingId, currentListingTypeId).catch(_temp2);
          }
        } else {
          router.push(ROUTER_LINKS.phoneAuth);
        }
      };
      $[20] = isListingBookmarked;
      $[21] = listingId;
      $[22] = listingType;
      $[23] = router;
      $[24] = t4;
    } else {
      t4 = $[24];
    }
    handleBookmarkClick = t4;
    t0 = images.length > 0 && <div className={cx(styles.listingDetailGallery, externalImages.length >= 4 && styles.listingDetailGallery__five, externalImages.length === 3 && styles.listingDetailGallery__four, externalImages.length === 2 && styles.listingDetailGallery__three, externalImages.length === 1 && styles.listingDetailGallery__two, externalImages.length === 0 && styles.listingDetailGallery__one)}>{isNew && <div className={styles.listingDetailGallery_isNew}>{listingLabelsTranslations("new")}</div>}<div className={styles.listingDetailGallery_relativeDate}>{relativeRefreshDate}</div>{externalImages.length > 0 && <button onClick={handleShowAllPhotos} className={styles.listingDetailGallery_showAllButton}>{listingDetailTranslations("show-all-photos")}</button>}<div className={styles.listingDetailGallery__mainImage}><Image src={mainImage.path} alt={alt} quality={75} fill={true} unoptimized={true} /></div>{externalImages.map((image, index) => <div key={image.id} className={styles[`listingDetailGallery__extra${(index + 1).toString()}`]}><Image src={image.path} alt={alt} quality={75} fill={true} /></div>)}</div>;
    $[0] = alt;
    $[1] = images;
    $[2] = isListingBookmarked;
    $[3] = isNew;
    $[4] = listingDetailTranslations;
    $[5] = listingId;
    $[6] = listingLabelsTranslations;
    $[7] = listingRefreshedDate;
    $[8] = listingType;
    $[9] = locale;
    $[10] = mainImage;
    $[11] = router;
    $[12] = handleBookmarkClick;
    $[13] = handleHideAllPhotos;
    $[14] = t0;
  } else {
    handleBookmarkClick = $[12];
    handleHideAllPhotos = $[13];
    t0 = $[14];
  }
  let t1;
  if ($[25] !== alt || $[26] !== handleBookmarkClick || $[27] !== handleHideAllPhotos || $[28] !== images || $[29] !== isAllPhotosShow || $[30] !== isListingBookmarked || $[31] !== isNew || $[32] !== listingId || $[33] !== listingRefreshedDate) {
    t1 = isAllPhotosShow && <ListingDetailAllPhotos images={images} alt={alt} onClose={handleHideAllPhotos} isBookmarked={isListingBookmarked} isNew={isNew} listingId={listingId} listingRefreshedDate={listingRefreshedDate} onBookmarkClick={handleBookmarkClick} />;
    $[25] = alt;
    $[26] = handleBookmarkClick;
    $[27] = handleHideAllPhotos;
    $[28] = images;
    $[29] = isAllPhotosShow;
    $[30] = isListingBookmarked;
    $[31] = isNew;
    $[32] = listingId;
    $[33] = listingRefreshedDate;
    $[34] = t1;
  } else {
    t1 = $[34];
  }
  let t2;
  if ($[35] !== t0 || $[36] !== t1) {
    t2 = <>{t0}{t1}</>;
    $[35] = t0;
    $[36] = t1;
    $[37] = t2;
  } else {
    t2 = $[37];
  }
  return t2;
}
function _temp2() {
  return null;
}
function _temp() {
  return null;
}